import React, { useEffect, useState } from "react";
import instance from "../../../../axios/instance";
import { getToken } from "../../../../utils/token";
import { useLoaderData } from "react-router-dom";
import EarningsChart from "../components/EarningsChart";
import { FilterItem } from "../components/FilterItem";
import { ArrowLeft2, ArrowRight2, SearchNormal, Sort } from "iconsax-react";
import { PaginationButton } from "../../../../components/PaginationButton";
import dayjs from "dayjs";

export const payoutsLoader = async () => {
  const token = getToken();
  const payouts = await instance.get(
    "/doctor/connect/payouts",
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );

  return {
    payouts: payouts.data,
  };
};

export const PayoutsPage = () => {
  const { payouts } = useLoaderData();
  const [filteredPayouts, setFilteredPayouts] = useState(payouts);
  const [_payouts, setPayouts] = useState(
    [...payouts].map((_, index) => ({
      ..._,
      index: index + 1,
    }))
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [pageIndex, setPageIndex] = useState(1);
  const [sorted, setSorted] = useState("");

  const filterPayouts = (payouts) => {
    setFilteredPayouts((items) =>
      payouts.filter((_, index) => index + 1 <= 10)
    );
  };

  const handleSearch = (e) => {
    e.preventDefault();

    const query = searchQuery.toLowerCase();
    setFilteredPayouts(() => {
      return _payouts.filter((payout) => {
        const id = `${payout.id}` === query;
        const status = payout.status.toLowerCase().includes(query);

        return id || status;
      });
    });
  };

  useEffect(() => {
    setFilteredPayouts(() =>
      _payouts.filter(
        (_, index) =>
          index + 1 > 10 * (pageIndex - 1) && index + 1 <= 10 * pageIndex
      )
    );
  }, [pageIndex]);

  const handleSort = (def) => {
    switch (def) {
      case "index": {
        setPageIndex(1);
        if (sorted !== "index") {
          setSorted("index");
        } else {
          setSorted("");
        }

        _payouts.reverse();
        setPayouts(_payouts);
        filterPayouts(_payouts);
        return;
      }
      case "amount": {
        setPageIndex(1);
        if (sorted === "amount") {
          setSorted("");
          _payouts.sort((a, b) => a.index - b.index);
        } else {
          setSorted("amount");
          _payouts.sort((a, b) => {
            if (a.amount < b.amount) return -1;
            if (a.amount > b.amount) return 1;
            return 0;
          });
        }
        filterPayouts(_payouts);
        return;
      }
      case "status": {
        setPageIndex(1);
        if (sorted === "status") {
          setSorted("");
          _payouts.sort((a, b) => a.index - b.index);
        } else {
          setSorted("status");
          _payouts.sort((a, b) => {
            if (a.status < b.status) return -1;
            if (a.status > b.status) return 1;
            return 0;
          });
        }
        filterPayouts(_payouts);
        return;
      }
      case "date": {
        setPageIndex(1);
        if (sorted === "date") {
          setSorted("");
          _payouts.sort((a, b) => a.index - b.index);
        } else {
          setSorted("date");
          _payouts.sort((a, b) => new Date(a.created) - new Date(b.created));
        }
        filterPayouts(_payouts);
        return;
      }
    }
  };

  return (
    <div className="mt-5 max-h-[83vh] overflow-y-scroll">
      <EarningsChart
        transactions={filteredPayouts}
        value="payouts"
        subtitle={`All Payouts`}
        title={`All Payouts Overview`}
        yOriginTitle={`All Payouts ($)`}
      />
      <div className="border bg-white rounded-md overflow-x-scroll scrollbar-hide mt-4">
        <div className="flex justify-between p-2 items-center">
          <form className="relative" onSubmit={handleSearch}>
            <input
              className="outline-none h-[40px] w-[300px] border rounded-md p-2 placeholder placeholder:text-[14px] text-sm"
              placeholder="Search payout"
              name="query"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button
              className="absolute h-[38px] rounded-md top-1/2 -translate-y-1/2 right-[1px] w-[30px] flex items-center justify-center bg-white cursor-pointer hover:text-primary duration-300"
              type="submit"
            >
              <SearchNormal size={15} />
            </button>
          </form>

          <div className="flex gap-2 items-center">
            <PaginationButton
              item={<ArrowLeft2 size={16} />}
              disabled={pageIndex <= 1}
              handleClick={() => setPageIndex(pageIndex - 1)}
            />
            <span className="font-light text-xs">Page {pageIndex}</span>
            <PaginationButton
              item={<ArrowRight2 size={16} />}
              disabled={pageIndex >= Math.ceil(_payouts.length / 10)}
              handleClick={() => setPageIndex((index) => index + 1)}
            />
          </div>
        </div>
        <div className="max-h-[80vh] overflow-y-scroll scrollbar-hide">
          <table className="w-full relative whitespace-nowrap">
            <thead>
              <tr className="uppercase h-14 font-bold bg-[#f1f1f1] text-gray-700 text-[12px]">
                <td
                  className="px-6 py-4 cursor-pointer hover:text-primary duration-300"
                  onClick={() => handleSort("index")}
                >
                  #{" "}
                  <Sort
                    size="15"
                    className={`${
                      sorted === "index" ? "rotate-180" : "rotate-0"
                    } inline`}
                    color="#FF8A65"
                  />
                </td>
                <td
                  className="px-6 py-4 cursor-pointer hover:text-primary duration-300"
                  onClick={() => handleSort("amount")}
                >
                  Amount{" "}
                  <Sort
                    size="15"
                    className={`${
                      sorted === "amount" ? "rotate-180" : "rotate-0"
                    } inline`}
                    color="#FF8A65"
                  />
                </td>
                <td
                  className="px-6 py-4 cursor-pointer hover:text-primary duration-300"
                  onClick={() => handleSort("status")}
                >
                  Status{" "}
                  <Sort
                    size="15"
                    className={`${
                      sorted === "status" ? "rotate-180" : "rotate-0"
                    } inline`}
                    color="#FF8A65"
                  />
                </td>
                <td
                  className="px-6 py-4 cursor-pointer hover:text-primary duration-300"
                  onClick={() => handleSort("date")}
                >
                  Date{" "}
                  <Sort
                    size="15"
                    className={`${
                      sorted === "date" ? "rotate-180" : "rotate-0"
                    } inline`}
                    color="#FF8A65"
                  />
                </td>
              </tr>
            </thead>

            <tbody>
              {filteredPayouts.length < 1 ? (
                <tr>
                  <td className="px-6 py-4" colSpan={6}>
                    No payouts yet
                  </td>
                </tr>
              ) : (
                filteredPayouts.map((payout, index) => (
                  <tr key={payout.id} className="h-14 text-[14px] border-b">
                    <td className="px-6 py-4">{payout.index}</td>
                    <td className="px-6 py-4">
                      ${(payout.amount).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 capitalize">{payout.status}</td>
                    <td className="px-6 py-4">
                      {dayjs(payout.created_at).format("DD MMM, YYYY")}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
